"use client"

import styled from "@emotion/styled"
import { Box, IconButton } from "@mui/material"
import { useScrollMinMax } from "pazChargeComponents/general/ScrollUpButton"
import {
  JSXElementConstructor,
  ReactElement,
  RefObject,
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from "react"
import { trackScrollerClick } from "reusable/ChargeGTM"
import colors from "theme/colors"
import { Desktop, NewText, useIsDesktop } from "ui"

const StyledFixed = styled(Box, {
  shouldForwardProp: (prop) =>
    !["noSectionsToShow", "smallDesktopMaxWidth"].includes(prop),
}) <{ noSectionsToShow: boolean, smallDesktopMaxWidth: number }>`

${(props) => props.theme.breakpoints.up("md")} {
  position: fixed;
  top: 142px;
  left: 0px;
  padding: 36px 0px;
  padding-left: 16px;
  width: 220px;
  display: ${(props) => props.noSectionsToShow ? "none" : "flex"};
  flex-direction: column;
  align-items: start;
  gap: 20px;
  border-left: 16px solid ${(props) => props.theme.colors.yellow};
  box-sizing: border-box;
  z-index: 100;
    padding-left: 11px;
    border-left: 10px solid ${(props) => props.theme.colors.yellow};
    max-width: ${(props) => props.smallDesktopMaxWidth}px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-left: 16px;
    border-left: 16px solid ${(props) => props.theme.colors.yellow};
    max-width: 200px;
  }
     ${(props) => props.theme.breakpoints.down("md")} {
      display: none;
  }
`

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    !["isSectionSelected"].includes(prop),
}) <{ isSectionSelected: boolean }>`
  padding: 0px;
  text-align: start;
  :hover {
    background-color: none;
  }
  :focus-visible {
    border: 1px solid ${colors.gray};
  }
  border-radius: 0px;
  ${(props) => props.theme.breakpoints.down("md")} {
    height: 100%;
    border-bottom: ${(props) =>
    props.isSectionSelected ? `4px solid ${props.theme.colors.yellow}` : 'none'};
  }
`

interface ScrollContextType {
  activeSection: string | null
  mobileFooterButonRefs: Record<string, RefObject<HTMLButtonElement>>
  scrollToSection: (sectionId: any) => void
  children: ReactElement<any, string | JSXElementConstructor<any>>[]
}

const ScrollContext = createContext<ScrollContextType | undefined>(undefined)

interface ScrollingProviderProps {
  children: ReactElement[]
  pageName?: string
  minOffsetShow?: number
  smallDesktopMaxWidth?: number
}


const generateRefsDictionary = <T extends HTMLElement>(
  children: ReactElement[]
): Record<string, RefObject<T>> => {
  return children?.reduce((refs, child) => {
    if (child.props.id) {
      refs[child.props.id] = useRef<T>(null);
    }
    return refs;
  }, {} as Record<string, RefObject<T>>);
};

export const ScrollingProvider: React.FC<ScrollingProviderProps> = ({
  children,
  pageName,
  minOffsetShow = 600,
  smallDesktopMaxWidth = 120
}) => {
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const scrolledChildren = children?.filter(child => child.key && !(child.key?.includes("page"))) || []
  const sectionRefs = generateRefsDictionary<HTMLDivElement>(scrolledChildren)
  const mobileFooterButonRefs = generateRefsDictionary<HTMLButtonElement>(scrolledChildren)
  const isDesktop = useIsDesktop()
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (scrolledChildren.length === 0) {
      return
    }
  }, [activeSection])
  useEffect(() => {
    if (!sectionRefs) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.length === 1) {
          return
        }
        // Find the entry with the maximum intersection ratio
        const mostVisibleEntry = entries.reduce((prev, curr) =>
          curr.intersectionRatio > prev.intersectionRatio ? curr : prev
        );

        // Find the entry with the maximum intersection ratio
        const inters = entries.map((item) =>
          item.intersectionRatio
        );

        // Only update if it’s significantly visible (e.g., 50% or more)
        if (mostVisibleEntry.intersectionRatio >= 0.5) {
          setActiveSection(mostVisibleEntry.target.id);
        }

        // Clear any pending debounce
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        // Debounce the update: wait 100ms before updating the active section.
        debounceRef.current = setTimeout(() => {
          // Optional: only update if the candidate is significantly visible (e.g., at least 50%)
          if (mostVisibleEntry.intersectionRatio >= 0.5 && mostVisibleEntry.target.id !== activeSection) {
            setActiveSection(mostVisibleEntry.target.id);
          }
        }, 500); // Adjust delay (in ms) as needed
      },
      {
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
        // rootMargin: "-25% -5% -30% -36%",
        rootMargin: "-90px 0px 0px 0px",
      },
    )

    Object.keys(sectionRefs).forEach((key) => {
      const curr = sectionRefs[key].current
      if (curr !== null) {
        observer.observe(curr)
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [sectionRefs])

  const scrollToSection = (sectionId: any) => {
    const sectionRef = sectionRefs[sectionId]
    if (sectionRef && sectionRef.current) {

      sectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setTimeout(() => {
        sectionRef.current?.focus({ preventScroll: true })
      }, 700) //
      // Move focus to the desired content
    }
  }
  // hook to have min-max values to display this scrolling provider
  const [showScroller] = useScrollMinMax(minOffsetShow, 1000, minOffsetShow === 0)

  const handleClick = (child: ReactElement<any, string | JSXElementConstructor<any>>) => {
    scrollToSection(child.props.id)
    trackScrollerClick(isDesktop ? "beneift" : "mobile_navigation_home_page", child.props.id, pageName || "Generic Page")
  }

  return (
    <ScrollContext.Provider value={{ activeSection, mobileFooterButonRefs, scrollToSection, children: scrolledChildren }}>
      <Desktop>
        <StyledFixed
          noSectionsToShow={scrolledChildren.length === 0 || !showScroller}
          smallDesktopMaxWidth={smallDesktopMaxWidth}
        >
          {scrolledChildren?.map((child, index) => (
            <StyledIconButton
              style={{ backgroundColor: "transparent", fontFamily: "inherit" }}
              key={index}
              onClick={() => handleClick(child)}
              isSectionSelected={activeSection === child.props.id}
              ref={mobileFooterButonRefs[child.props.id]}
            >
              <NewText
                desktopFontSize={activeSection === child.props.id ? 18 : 16}
                largeDesktopFontSize={activeSection === child.props.id ? 20 : 16}
                largeDesktopLineHeight={
                  activeSection === child.props.id ? "28px" : "22.4px"
                }
                mobileFontSize={16}
                component={"p"}
                desktopFontWeight={
                  activeSection === child.props.id ? "600" : "400"
                }
                desktopLineHeight={
                  activeSection === child.props.id ? "19.6px" : "16.8px"
                }
              >
                {child.props.id}
              </NewText>
            </StyledIconButton>
          ))}
        </StyledFixed>
      </Desktop>
      {children?.map((child) =>
        cloneElement(child, {
          ref: sectionRefs[child.props.id],
          tabIndex: -1, // Make the element focusable
        }),
      )}
    </ScrollContext.Provider>
  )
}

export const useScroll = () => {
  const context = useContext(ScrollContext)
  if (context === undefined) {
    throw new Error("useScroll must be used within a ScrollingProvider")
  }
  return context
}
